import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuList, NavigationMenuTrigger } from "./ui/navigation-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Menu, Building, TrendingUp, Zap, Wind, Sun, Settings, ChevronDown } from "lucide-react";
import { products } from "../mock";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLiftDropdownOpen, setIsLiftDropdownOpen] = useState(false);
  const [isMobileLiftOpen, setIsMobileLiftOpen] = useState(false);
  const location = useLocation();

  const iconMap = {
    Building,
    TrendingUp,
    Zap,
    Wind,
    Sun,
    Settings
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLiftDropdownToggle = () => {
    setIsLiftDropdownOpen(!isLiftDropdownOpen);
  };

  const closeLiftDropdown = () => {
    setIsLiftDropdownOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    window.location.href = '/';
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-gray-200" role="banner">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center space-x-3 cursor-pointer"
            aria-label="G.A Technology & Power City - Homepage"
            onClick={handleLogoClick}
          >
            <img 
              src="/logo.jpg" 
              alt="G.A Technology & Power City Logo" 
              className="h-12 w-12 object-contain rounded-lg"
            />
            <div className="hidden sm:block">
              <span className="text-xl font-bold text-gray-900">G.A Technology</span>
              <span className="text-sm text-gray-600 block">& Power City</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link
                    to="/"
                    className={`px-3 py-2 text-sm font-medium transition-colors hover:text-blue-600 ${
                      isActive("/") ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    Home
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link
                    to="/about"
                    className={`px-3 py-2 text-sm font-medium transition-colors hover:text-blue-600 ${
                      isActive("/about") ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    About Us
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Popover open={isLiftDropdownOpen} onOpenChange={setIsLiftDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="text-sm font-medium text-gray-700 hover:text-blue-600 px-3 py-2 h-auto"
                        onClick={handleLiftDropdownToggle}
                      >
                        Products & Services
                        <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isLiftDropdownOpen ? 'rotate-180' : ''}`} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-0 max-h-[80vh]" align="start">
                      <div className="max-h-[75vh] overflow-y-auto p-4">
                        <div className="grid gap-2">
                          {/* Lift Section - Main Categories */}
                          <div className="mb-4">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 sticky top-0 z-10">
                              <Building className="h-5 w-5 text-blue-600" />
                              <div>
                                <div className="font-semibold text-gray-900">Lift Solutions</div>
                                <div className="text-sm text-gray-600">Complete range of lift and escalator options</div>
                              </div>
                            </div>
                            
                            <div className="ml-8 mt-2">
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  {name: "Passenger Lift", key: "passenger-lift"},
                                  {name: "Capsule Lift", key: "capsule-lift"},
                                  {name: "Hospital Lift", key: "hospital-lift"},
                                  {name: "Cargo Lift", key: "cargo-lift"},
                                  {name: "Escalator", key: "escalator"}
                                ].map((type) => (
                                  <Link
                                    key={type.key}
                                    to={`/products/lift/${type.key}`}
                                    className="text-xs text-blue-600 hover:text-blue-800 py-3 px-3 rounded hover:bg-blue-50 transition-colors block text-center border border-blue-200 hover:border-blue-400 bg-blue-25"
                                    onClick={closeLiftDropdown}
                                  >
                                    {type.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Other Products */}
                          <div className="border-t pt-3">
                            <div className="font-medium text-gray-800 text-sm mb-2 sticky top-16 bg-white py-1 z-10">Other Services</div>
                            {products.slice(1).map((product) => {
                              const IconComponent = iconMap[product.icon];
                              return (
                                <Link
                                  key={product.id}
                                  to={`/products/${product.id}`}
                                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200"
                                  onClick={closeLiftDropdown}
                                >
                                  <IconComponent className="h-5 w-5 text-blue-600 flex-shrink-0" />
                                  <div>
                                    <div className="font-medium text-gray-900">{product.name}</div>
                                    <div className="text-sm text-gray-600 line-clamp-1">
                                      {product.description}
                                    </div>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                          
                          <div className="border-t pt-3 sticky bottom-0 bg-white">
                            <Link
                              to="/products"
                              className="block w-full text-center py-3 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                              onClick={closeLiftDropdown}
                            >
                              View All Products →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/projects"
                    className={`px-3 py-2 text-sm font-medium transition-colors hover:text-blue-600 ${
                      isActive("/projects") ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    Projects
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/media"
                    className={`px-3 py-2 text-sm font-medium transition-colors hover:text-blue-600 ${
                      isActive("/media") ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    Media
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/certificates"
                    className={`px-3 py-2 text-sm font-medium transition-colors hover:text-blue-600 ${
                      isActive("/certificates") ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    Certificates
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* Contact Button & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <Link to="/contact">
              <Button className="hidden sm:inline-flex bg-blue-600 hover:bg-blue-700 text-white">
                Contact Us
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="outline" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 flex flex-col">
                <div className="flex-1 overflow-hidden">
                  <div className="h-full overflow-y-auto">
                    <div className="flex flex-col space-y-4 mt-6 pb-4">
                      <Link
                        to="/"
                        onClick={() => setIsOpen(false)}
                        className={`px-3 py-2 text-sm font-medium ${
                          isActive("/") ? "text-blue-600" : "text-gray-700"
                        }`}
                      >
                        Home
                      </Link>
                      <Link
                        to="/about"
                        onClick={() => setIsOpen(false)}
                        className={`px-3 py-2 text-sm font-medium ${
                          isActive("/about") ? "text-blue-600" : "text-gray-700"
                        }`}
                      >
                        About Us
                      </Link>
                      
                      <div className="space-y-2">
                        <button
                          onClick={() => setIsMobileLiftOpen(!isMobileLiftOpen)}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-900 bg-gray-50 rounded"
                        >
                          <div className="flex items-center">
                            <Building className="h-4 w-4 mr-2" />
                            Products & Services
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${isMobileLiftOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isMobileLiftOpen && (
                          <div className="space-y-1 pl-3 max-h-96 overflow-y-auto">
                            {/* Lift Section - Main Categories */}
                            <div className="space-y-2">
                              <div className="px-3 py-2 text-sm font-medium text-gray-800 bg-blue-50 rounded sticky top-0 z-10">
                                <Building className="h-4 w-4 inline mr-2" />
                                Lift Solutions
                              </div>
                              
                              <div className="pl-6">
                                <div className="grid grid-cols-1 gap-2">
                                  {[
                                    {name: "Passenger Lift", key: "passenger-lift"},
                                    {name: "Capsule Lift", key: "capsule-lift"},
                                    {name: "Hospital Lift", key: "hospital-lift"},
                                    {name: "Cargo Lift", key: "cargo-lift"},
                                    {name: "Escalator", key: "escalator"}
                                  ].map((type) => (
                                    <Link
                                      key={type.key}
                                      to={`/products/lift/${type.key}`}
                                      onClick={() => {
                                        setIsOpen(false);
                                        setIsMobileLiftOpen(false);
                                      }}
                                      className="block px-3 py-3 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 hover:border-blue-400"
                                    >
                                      {type.name}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Other Products */}
                            <div className="pt-3 border-t border-gray-200">
                              <div className="text-xs font-medium text-gray-700 py-2 sticky top-10 bg-white">Other Services</div>
                              {products.slice(1).map((product) => {
                                const IconComponent = iconMap[product.icon];
                                return (
                                  <Link
                                    key={product.id}
                                    to={`/products/${product.id}`}
                                    onClick={() => {
                                      setIsOpen(false);
                                      setIsMobileLiftOpen(false);
                                    }}
                                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-600 hover:text-blue-600 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 mb-1"
                                  >
                                    <IconComponent className="h-4 w-4 flex-shrink-0" />
                                    <span className="truncate">{product.name}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      <Link
                        to="/projects"
                        onClick={() => setIsOpen(false)}
                        className={`px-3 py-2 text-sm font-medium ${
                          isActive("/projects") ? "text-blue-600" : "text-gray-700"
                        }`}
                      >
                        Projects
                      </Link>
                      <Link
                        to="/media"
                        onClick={() => setIsOpen(false)}
                        className={`px-3 py-2 text-sm font-medium ${
                          isActive("/media") ? "text-blue-600" : "text-gray-700"
                        }`}
                      >
                        Media
                      </Link>
                      <Link
                        to="/certificates"
                        onClick={() => setIsOpen(false)}
                        className={`px-3 py-2 text-sm font-medium ${
                          isActive("/certificates") ? "text-blue-600" : "text-gray-700"
                        }`}
                      >
                        Certificates
                      </Link>
                      <Link
                        to="/contact"
                        onClick={() => setIsOpen(false)}
                        className="px-3 py-2"
                      >
                        <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                          Contact Us
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;